import { useState } from 'react';
import { Trophy, Gamepad2, Award } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { getRanking, calculateRankingDates, RankingData } from '@/api/rankingApi';
import { useToast } from '@/components/ui/use-toast';

interface RankingPopoverProps {
  children: React.ReactNode;
}

/**
 * 월간 랭킹 Popover
 * 지난달 TOP 3 및 이번달 전체 랭킹 표시
 */
export default function RankingPopover({ children }: RankingPopoverProps) {
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastRanking, setLastRanking] = useState<RankingData[]>([]);
  const [thisRanking, setThisRanking] = useState<RankingData[]>([]);
  const [lastMonthRange, setLastMonthRange] = useState('');
  const [thisMonthRange, setThisMonthRange] = useState('');

  /**
   * Popover가 열릴 때 랭킹 데이터 로드
   */
  const handleOpenChange = async (isOpen: boolean) => {
    setOpen(isOpen);

    if (isOpen && lastRanking.length === 0 && thisRanking.length === 0) {
      await loadRankings();
    }
  };

  const loadRankings = async () => {
    try {
      setLoading(true);
      const { params, lastMonthRange, thisMonthRange } = calculateRankingDates();

      const data = await getRanking(params);

      setLastRanking(data.getLastRanking);
      setThisRanking(data.getThisRanking);
      setLastMonthRange(lastMonthRange);
      setThisMonthRange(thisMonthRange);
    } catch (error) {
      console.error('랭킹 로드 실패:', error);
      toast({
        title: '오류',
        description: '랭킹 정보를 불러오는데 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 순위에 따른 메달 또는 숫자 반환
   */
  const getRankingDisplay = (index: number) => {
    switch (index) {
      case 0:
        return '🥇';
      case 1:
        return '🥈';
      case 2:
        return '🥉';
      default:
        return `${index + 1}`;
    }
  };

  /**
   * 지난달 TOP 3 카드
   */
  const renderLastMonthTop3 = () => {
    if (lastRanking.length === 0) {
      return <p className="text-xs text-muted-foreground text-center py-2">데이터가 없습니다.</p>;
    }

    return (
      <div className="space-y-2">
        {lastRanking.slice(0, 3).map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-2 p-2 rounded-md bg-gradient-to-r from-accent/20 to-accent/5 border border-accent/30"
          >
            <div className="text-xl flex-shrink-0">{getRankingDisplay(index)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{item.nickname}</p>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground flex-shrink-0">
              <Gamepad2 className="w-3 h-3" />
              <span className="text-xs font-medium">{item.totalCount}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /**
   * 이번달 전체 랭킹 리스트
   */
  const renderThisMonthRanking = () => {
    if (thisRanking.length === 0) {
      return <p className="text-xs text-muted-foreground text-center py-2">데이터가 없습니다.</p>;
    }

    return (
      <div className="space-y-1.5">
        {thisRanking.map((item, index) => {
          const isTop3 = index < 3;
          return (
            <div
              key={index}
              className={`flex items-center gap-2 p-1.5 rounded-sm transition-colors ${
                isTop3
                  ? 'bg-gradient-to-r from-accent/20 to-accent/5 border border-accent/30'
                  : 'bg-secondary/30 hover:bg-secondary/50'
              }`}
            >
              <div className={`flex-shrink-0 w-6 text-center ${isTop3 ? 'text-lg' : 'text-xs font-medium'}`}>
                {getRankingDisplay(index)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${isTop3 ? 'font-semibold' : 'font-medium'} text-xs truncate`}>{item.nickname}</p>
              </div>
              <div className="flex items-center gap-1 text-muted-foreground flex-shrink-0">
                <Gamepad2 className="w-3 h-3" />
                <span className="text-xs font-medium">{item.totalCount}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 space-y-3">
          {/* 헤더 */}
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <h3 className="font-bold text-sm">월간 랭킹</h3>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <p className="text-xs text-muted-foreground">로딩 중...</p>
            </div>
          ) : (
            <>
              {/* 지난달 TOP 3 */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5 text-yellow-500" />
                  <h4 className="font-semibold text-xs">지난달 랭킹</h4>
                </div>
                <p className="text-xs text-muted-foreground">{lastMonthRange}</p>
                {renderLastMonthTop3()}
              </div>

              <Separator />

              {/* 이번달 전체 랭킹 */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Trophy className="h-3.5 w-3.5 text-primary" />
                  <h4 className="font-semibold text-xs">이번달 랭킹</h4>
                  <Badge variant="secondary" className="text-xs px-1 py-0">
                    진행중
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{thisMonthRange}</p>
                <div className="max-h-60 overflow-y-auto scrollbar-hover">
                  {renderThisMonthRanking()}
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
